import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { 
  FiArrowLeft, FiLoader, FiCpu, FiFileText, 
  FiCheckCircle, FiAward, FiTarget, FiChevronRight, FiBarChart2,
  FiEdit, FiDownload, FiCheck, FiSave, FiBriefcase, FiX, FiPlus, FiXCircle
} from 'react-icons/fi';
import ProgressBar from '../../components/common/ProgressBar';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import { useMembership } from '../../contexts/MembershipContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBalance } from '../../contexts/BalanceContext';
import { useCache } from '../../contexts/CacheContext';
import ResumeComparison from '../../pages/resumes/ResumeComparison';
import ResumeDocument from '../../pages/resumes/ResumeDocument';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisabledCreditButton from '../../components/membership/DisabledCreditButton';
import SectionSelector from '../../components/resumes/SectionSelector';
import jobBoardApi from '../../services/jobBoardApi';
import tourService from '../../services/tourService';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const ResumeMassage = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams();
  const { refreshCreditBalance } = useMembership();
  const { currentUser } = useAuth();
  const { getResumes, invalidateResumeCache } = useCache();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [polishingProgress, setPolishingProgress] = useState(0);
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [resumeVersions, setResumeVersions] = useState([]);
  const [analysisCreditCost, setAnalysisCreditCost] = useState(5);
  const [polishCreditCost, setPolishCreditCost] = useState(10);
  
  const [jobRecommendations, setJobRecommendations] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [polishing, setPolishing] = useState(false);
  const [polishResults, setPolishResults] = useState(null);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [showResumeEdit, setShowResumeEdit] = useState(false);
  const [originalResumeContent, setOriginalResumeContent] = useState(null);
  const [polishedResumeContent, setPolishedResumeContent] = useState(null);
  const [selectedSections, setSelectedSections] = useState(['summary', 'skills', 'experience']);
  const [selectedExperienceIndexes, setSelectedExperienceIndexes] = useState([]);
  
  // Follow-up questions state (similar to JobApplication.js)
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [followUpAnswers, setFollowUpAnswers] = useState({});
  const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  // Step 1: Job Recommendations
  // Step 2: Strengths and Weaknesses with Additional Info
  // Step 3: Compare Original and Polished Resume
  // Step 4: Final Actions (Compare, Edit, Find Jobs)
  
  // Step 4 state
  const [activeTab, setActiveTab] = useState('compare');
  const [showJobSearchModal, setShowJobSearchModal] = useState(false);
  const [saving, setSaving] = useState(false);


  useEffect(() => {
    fetchResumes();
  }, []);
  
  useEffect(() => {
    if (currentUser?.default_polish_instructions && !additionalInfo) {
      setAdditionalInfo(currentUser.default_polish_instructions);
    }
  }, [currentUser, additionalInfo]);
  
  useEffect(() => {
    if (selectedResume) {
      loadResumeVersions(selectedResume);
    }
  }, [selectedResume]);

  useEffect(() => {
    setSelectedExperienceIndexes([]);
  }, [selectedResume, selectedVersion]);

  // Start tour when component mounts
  useEffect(() => {
    if (!tourService.isTourCompleted('resume_massage')) {
      const timer = setTimeout(() => {
        tourService.startTour('resume_massage');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const resumes = await getResumes();
      setResumes(resumes);
      
      // If resumeId is provided in URL, select that resume
      if (resumeId && resumes.some(r => r.id === resumeId)) {
        setSelectedResume(resumeId);
      } else if (resumes.length > 0) {
        setSelectedResume(resumes[0].id);
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };
  
  const loadResumeVersions = async (resumeId) => {
    try {
      const response = await resumeApi.getResumeById(resumeId);
      const { versions } = response.data;
      setResumeVersions(versions);
      
      if (versions.length > 0) {
        // Select the first version by default
        setSelectedVersion(versions[0].versionId);
      } else {
        setSelectedVersion('');
      }
    } catch (error) {
      console.error('Error loading resume versions:', error);
      toast.error('Failed to load resume versions');
    }
  };
  
  const handleResumeChange = (e) => {
    const resumeId = e.target.value;
    setSelectedResume(resumeId);
    // Reset job recommendations when resume changes
    setJobRecommendations(null);
    setSelectedJobType(null);
    setPolishResults(null);
    setCurrentStep(1);
  };
  
  const handleVersionChange = (e) => {
    setSelectedVersion(e.target.value);
    // Reset job recommendations when version changes
    setJobRecommendations(null);
    setSelectedJobType(null);
    setPolishResults(null);
    setCurrentStep(1);
  };
  
  const analyzeResume = async () => {
    if (!selectedResume || !selectedVersion) {
      toast.error('Please select a resume and version');
      return;
    }
    
    let progressInterval;
    try {
      setAnalyzing(true);
      setAnalysisProgress(0);
      
      // Simulate progress updates
      progressInterval = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 90) {
            return prev;
          }
          return prev + 10;
        });
      }, 500);
      
      // Get resume content for direct passing
      let resumeContent = null;
      try {
        // Find the selected resume version content
        const selectedResumeObj = resumes.find(r => r.id === selectedResume);
        if (selectedResumeObj) {
          const selectedVersionObj = resumeVersions.find(v => v.versionId === selectedVersion);
          if (selectedVersionObj && selectedVersionObj.content) {
            resumeContent = selectedVersionObj.content;
          }
        }
        
        // If not found in memory, fetch it
        if (!resumeContent) {
          const contentResponse = await resumeApi.getResumeVersion(selectedResume, selectedVersion);
          if (contentResponse && contentResponse.data && contentResponse.data.content) {
            resumeContent = contentResponse.data.content;
          }
        }
      } catch (err) {
        console.error('Error getting resume content:', err);
      }
      
      // Call the API endpoint to analyze the resume and get job recommendations
      const response = await resumeApi.getJobRecommendations(selectedResume, selectedVersion, resumeContent);
      
      // Refresh credit balance after successful analysis
      await refreshCreditBalance();
      
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      
      setJobRecommendations(response.data);
      // Move to step 1 after analysis
      setCurrentStep(1);
    } catch (error) {
      console.error('Error analyzing resume:', error);
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to analyze resume');
      }
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setAnalyzing(false);
      setTimeout(() => setAnalysisProgress(0), 1000);
    }
  };
  
  const selectJobType = (jobType) => {
    setSelectedJobType(jobType);
    
    // Set up follow-up questions if they exist
    if (jobType.followUpQuestions && jobType.followUpQuestions.length > 0) {
      setFollowUpQuestions(jobType.followUpQuestions);
      setFollowUpAnswers({});
      setShowFollowUpQuestions(false);
      setCurrentQuestionIndex(0);
    } else {
      setFollowUpQuestions([]);
      setFollowUpAnswers({});
      setShowFollowUpQuestions(false);
    }
    
    // Move to step 2 after selecting a job type
    setCurrentStep(2);
  };
  
  // Follow-up question handlers (similar to JobApplication.js)
  const handleFollowUpAnswer = (questionIndex, answer) => {
    setFollowUpAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };
  
  const handleNextQuestion = () => {
    if (currentQuestionIndex < followUpQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };
  
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  
  const handleShowFollowUpQuestions = () => {
    setShowFollowUpQuestions(true);
    setCurrentQuestionIndex(0);
  };
  
  const handleHideFollowUpQuestions = () => {
    setShowFollowUpQuestions(false);
    setCurrentQuestionIndex(0);
  };
  
  // Format follow-up questions and answers into additional information
  const formatFollowUpInfo = () => {
    if (followUpQuestions.length === 0 || Object.keys(followUpAnswers).length === 0) {
      return '';
    }
    
    const followUpSection = followUpQuestions
      .map((question, index) => {
        const answer = followUpAnswers[index] || '';
        if (answer.trim()) {
          return `Q: ${question}\nA: ${answer}`;
        }
        return null;
      })
      .filter(Boolean)
      .join('\n\n');
    
    return followUpSection ? `--- Follow-up Information ---\n\n${followUpSection}` : '';
  };

  const handleAdditionalInfoSubmit = () => {
    // Validate if a job type is selected
    if (!selectedJobType) {
      toast.error('Please select a job type first');
      return;
    }
    
    // Proceed to polish the resume with the additional info
    polishResumeForJobType();
  };
  
  const polishResumeForJobType = async () => {
    if (!selectedJobType) {
      toast.error('Please select a job type first');
      return;
    }
    
    let progressInterval;
    try {
      setPolishing(true);
      setPolishingProgress(0);
      
      // Simulate progress updates
      progressInterval = setInterval(() => {
        setPolishingProgress(prev => {
          if (prev >= 90) {
            return prev;
          }
          return prev + 5;
        });
      }, 300);
      
      // Store the original resume content for comparison
      let originalContent = null;
      try {
        const originalResponse = await resumeApi.getResumeVersion(selectedResume, selectedVersion);
        originalContent = originalResponse.data.content;
        setOriginalResumeContent(originalContent);
      } catch (err) {
        console.error('Error fetching original resume content:', err);
      }
      
      // Format combined additional information
      const followUpInfo = formatFollowUpInfo();
      const combinedAdditionalInfo = additionalInfo || followUpInfo 
        ? `${additionalInfo}\n\n${followUpInfo}`.trim()
        : '';
      
      // Call the API endpoint to polish the resume for the selected job type
      const experienceIndexes = selectedExperienceIndexes.length > 0 ? selectedExperienceIndexes : null;
      const response = await resumeApi.polishResumeForJobType(
        selectedResume,
        selectedVersion,
        selectedJobType.id,
        jobRecommendations,
        combinedAdditionalInfo, // Pass the combined additional info to the API
        originalContent || originalResumeContent, // Pass resume content directly
        followUpAnswers, // Pass follow-up answers
        selectedSections,
        experienceIndexes
      );
      
      // Invalidate the resume cache since we've created a new version
      invalidateResumeCache();
      
      setPolishResults(response.data);
      
      // Fetch the polished resume content for comparison
      try {
        const polishedResponse = await resumeApi.getResumeVersion(
          response.data.resumeId, 
          response.data.versionId
        );
        setPolishedResumeContent(polishedResponse.data.content);
      } catch (err) {
        console.error('Error fetching polished resume content:', err);
      }
      
      // Refresh credit balance after successful polish
      await refreshCreditBalance();
      
      setPolishingProgress(100);
      
      // Move to step 4 after polishing
      setCurrentStep(4);
      toast.success('Resume has been polished successfully!');
    } catch (error) {
      console.error('Error polishing resume:', error);
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to polish resume');
      }
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setPolishing(false);
      setTimeout(() => setPolishingProgress(0), 1000);
    }
  };

  const toggleResumeEdit = () => {
    setShowResumeEdit(!showResumeEdit);
  };
  
  const navigateToNewVersion = () => {
    if (polishResults && polishResults.resumeId) {
      navigate(`/resumes/${polishResults.resumeId}`);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowJobSearchModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFindJobs = async () => {
    try {
      // Search for jobs based on the selected job type
      const searchQuery = selectedJobType.title;
      const response = await jobBoardApi.searchJobs(searchQuery, null, null, null, 20);
      
      // Navigate to job board with search results
      navigate('/jobs', { 
        state: { 
          searchResults: response.data,
          searchQuery: searchQuery,
          fromResumeMassage: true
        }
      });
    } catch (error) {
      console.error('Error searching jobs:', error);
      toast.error('Failed to search for jobs');
      // Navigate to job board anyway
      navigate('/jobs');
    }
  };
  

  
  const generateResumePDF = async () => {
    if (!polishResults || !polishResults.resumeId || !polishResults.versionId) {
      toast.error('No polished resume available to download');
      return;
    }
    
    setShowStyleModal(true);
  };
  
  // Helper function to format score as a percentage
  const formatScore = (score) => {
    return `${Math.round(score * 100)}%`;
  };
  
  // Helper function to get color based on match score
  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Function to render progress steps
  const renderProgressSteps = () => {
    const steps = [
      { id: 1, name: 'Job Recommendations' },
      { id: 2, name: 'Strengths & Weaknesses' },
      { id: 3, name: 'Polish Resume' },
      { id: 4, name: 'Final Actions' }
    ];
    
    return (
      <div className="flex items-center justify-between w-full px-4">
        {steps.map((step) => (
          <div key={step.id} className="flex flex-col items-center">
            {/* Step circle */}
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep >= step.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > step.id ? <FiCheck className="w-5 h-5" /> : step.id}
            </div>
            
            {/* Step name */}
            <span 
              className={`mt-2 text-sm font-medium ${
                currentStep >= step.id ? 'text-primary-600' : 'text-gray-500'
              }`}
              style={{ whiteSpace: 'nowrap' }}
            >
              {step.name}
            </span>
          </div>
        ))}
      </div>
    );
  };
  
  const experienceOptions = (
    resumeVersions.find(v => v.versionId === selectedVersion)?.content?.experience ||
    originalResumeContent?.experience ||
    []
  );
  
  if (loading) {
    return <LoadingSpinner message="Loading resume data..." />;
  }
  
  const copy = pageDescriptions.resumeMassage;

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <Link
            to={resumeId ? `/resumes/${resumeId}` : "/resumes"}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft className="mr-2 h-4 w-4" />
            {resumeId ? "Back to Resume" : "Back to Resumes"}
          </Link>
        }
      />
      
      <div className="card overflow-hidden mb-8">
        {/* Progress indicator */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          {renderProgressSteps()}
        </div>
        
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiCpu className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">How this works</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Our AI will analyze your resume and recommend the most suitable job types based on your skills and experience. You can then select a job type to receive personalized feedback and have your resume automatically tailored to enhance your chances in that field.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Resume Selection - Always visible */}
            <div>
              <label htmlFor="resume-select" className="block text-sm font-medium text-gray-700">
                Select a resume to analyze
              </label>
              
              {resumes.length > 0 ? (
                <div className="mt-1">
                  <select
                    id="resume-select"
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    value={selectedResume}
                    onChange={handleResumeChange}
                  >
                    {resumes.map(resume => (
                      <option key={resume.id} value={resume.id}>
                        {resume.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mt-2 text-sm text-red-600">
                  No resumes found. Please upload a resume first.
                  <div className="mt-2">
                    <Link
                      to="/resumes/upload"
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <FiFileText className="-ml-0.5 mr-2 h-4 w-4" />
                      Upload Resume
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            {/* Version Selection - Always visible */}
            {selectedResume && resumeVersions.length > 0 && (
              <div>
                <label htmlFor="version-select" className="block text-sm font-medium text-gray-700">
                  Select Resume Version
                </label>
                <select
                  id="version-select"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={selectedVersion}
                  onChange={handleVersionChange}
                >
                  {resumeVersions.map(version => (
                    <option key={version.versionId} value={version.versionId}>
                      {version.customName || version.versionId} ({version.versionType})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Analyze Button - Always visible if no recommendations yet */}
            {selectedResume && selectedVersion && !jobRecommendations && (
              <div className="flex justify-center">
                <div className="relative" style={{ minHeight: analyzing ? '100px' : 'auto' }}>
                  <DisabledCreditButton
                    serviceType="resume_analysis"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={analyzeResume}
                    disabled={analyzing}
                  >
                    {analyzing ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Analyzing Resume...
                      </>
                    ) : (
                      <>
                        <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                        Analyze Resume ({analysisCreditCost} credits)
                      </>
                    )}
                  </DisabledCreditButton>
                  {analyzing && (
                    <ProgressBar progress={analysisProgress} />
                  )}
                </div>
              </div>
            )}
            
            {/* Step 1: Job Recommendations */}
            {currentStep === 1 && jobRecommendations && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Step 1: Select a Job Type for Resume Optimization
                </h3>
                
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                  {jobRecommendations.recommendations.map((job, index) => (
                    <div 
                      key={index}
                      className={`border rounded-lg p-4 cursor-pointer hover:border-primary-500 hover:shadow-md transition-all ${
                        selectedJobType?.id === job.id ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500' : 'border-gray-200'
                      }`}
                      onClick={() => selectJobType(job)}
                    >
                      <div className="flex justify-between items-start">
                        <h4 className="text-base font-medium text-gray-900">{job.title}</h4>
                        <span className={`text-sm font-semibold ${getScoreColor(job.matchScore)}`}>
                          {formatScore(job.matchScore)} Match
                        </span>
                      </div>
                      
                      <p className="mt-2 text-sm text-gray-600">
                        {job.description}
                      </p>
                      
                      <div className="mt-3">
                        <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                          Key Skills
                        </h5>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {job.keySkills.map((skill, idx) => (
                            <span 
                              key={idx}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                skill.present 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800 line-through opacity-75'
                              }`}
                            >
                              {skill.present ? <FiCheckCircle className="mr-1 h-3 w-3" /> : null}
                              {skill.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectJobType(job);
                          }}
                        >
                          {selectedJobType?.id === job.id ? 'Selected' : 'Select'}
                          <FiChevronRight className="ml-1 h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Step 2: Strengths and Weaknesses with Additional Info */}
      {currentStep === 2 && selectedJobType && (
        <div className="card overflow-hidden mb-8">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h2 className="text-xl font-medium text-gray-900">
              Step 2: Strengths & Weaknesses for {selectedJobType.title}
            </h2>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              {/* Gap Analysis */}
              <div>
                <h3 className="text-base font-medium text-gray-900 flex items-center">
                  <FiBarChart2 className="mr-2 h-5 w-5 text-primary-500" />
                  Skills Gap Analysis
                </h3>
                
                <div className="mt-4 border border-gray-200 rounded-lg p-4">
                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Strengths</h4>
                      <ul className="mt-2 space-y-1 text-sm text-gray-600">
                        {selectedJobType.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start">
                            <FiCheckCircle className="mt-0.5 mr-2 h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Areas to Improve</h4>
                      <ul className="mt-2 space-y-1 text-sm text-gray-600">
                        {selectedJobType.areasToImprove.map((area, idx) => (
                          <li key={idx} className="flex items-start">
                            <FiTarget className="mt-0.5 mr-2 h-4 w-4 text-orange-500 flex-shrink-0" />
                            <span>{area}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Selection */}
              <div>
                <h3 className="text-base font-medium text-gray-900 flex items-center">
                  <FiEdit className="mr-2 h-5 w-5 text-primary-500" />
                  Sections to Polish
                </h3>
                
                <div className="mt-2 text-sm text-gray-600">
                  <p>Select which sections of your resume you want to enhance for {selectedJobType.title} roles:</p>
                </div>
                
                <div className="mt-4">
                  <SectionSelector 
                    selectedSections={selectedSections}
                    onChange={setSelectedSections}
                  />
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700">Experience Items (Optional)</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Choose specific experiences to polish. Leave empty to polish all experiences.
                </p>
                <div className="mt-3 space-y-2">
                  {experienceOptions.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No experience items found.</p>
                  ) : (
                    experienceOptions.map((exp, index) => {
                      const label = [exp.position, exp.company].filter(Boolean).join(' at ') || `Experience ${index + 1}`;
                      const isChecked = selectedExperienceIndexes.includes(index);
                      return (
                        <label key={`exp-${index}`} className="flex items-center space-x-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            checked={isChecked}
                            onChange={(e) => {
                              setSelectedExperienceIndexes(prev => {
                                if (e.target.checked) {
                                  return [...prev, index];
                                }
                                return prev.filter(i => i !== index);
                              });
                            }}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                {experienceOptions.length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:text-primary-700"
                      onClick={() => setSelectedExperienceIndexes(experienceOptions.map((_, i) => i))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-600 hover:text-gray-700"
                      onClick={() => setSelectedExperienceIndexes([])}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              
              {/* Follow-up Questions Section */}
              {followUpQuestions.length > 0 && (
                <div className="mb-6">
                  {!showFollowUpQuestions ? (
                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Want to improve your resume with targeted questions?
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Answer {followUpQuestions.length} follow-up question{followUpQuestions.length > 1 ? 's' : ''} to provide more context about your qualifications for {selectedJobType.title} roles.
                      </p>
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={handleShowFollowUpQuestions}
                      >
                        <FiPlus className="-ml-1 mr-2 h-4 w-4" />
                        Add More Information
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-medium text-blue-800">
                          Question {currentQuestionIndex + 1} of {followUpQuestions.length}
                        </h4>
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-800"
                          onClick={handleHideFollowUpQuestions}
                        >
                          <FiXCircle className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-3">
                          {followUpQuestions[currentQuestionIndex]}
                        </label>
                        <textarea
                          rows={4}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="Your answer..."
                          value={followUpAnswers[currentQuestionIndex] || ''}
                          onChange={(e) => handleFollowUpAnswer(currentQuestionIndex, e.target.value)}
                        />
                      </div>
                      
                      {/* Navigation Buttons */}
                      <div className="flex justify-between mt-6">
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          onClick={handlePreviousQuestion}
                          disabled={currentQuestionIndex === 0}
                        >
                          <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                          Back
                        </button>
                        
                        <div className="flex space-x-2">
                          {currentQuestionIndex < followUpQuestions.length - 1 ? (
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              onClick={handleNextQuestion}
                            >
                              Next
                              <FiArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              onClick={handleHideFollowUpQuestions}
                            >
                              <FiCheck className="-ml-1 mr-2 h-4 w-4" />
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Information Input */}
              <div>
                <h3 className="text-base font-medium text-gray-900 flex items-center">
                  <FiAward className="mr-2 h-5 w-5 text-primary-500" />
                  Additional Information
                </h3>
                
                <div className="mt-2 text-sm text-gray-600">
                  <p>Add any additional information that might help tailor your resume for {selectedJobType.title} roles:</p>
                </div>
                
                <div className="mt-4">
                  <textarea
                    id="additional-info"
                    name="additional-info"
                    rows={5}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Describe any additional skills, experiences, or qualifications that may be relevant for this position..."
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    This information will be used to enhance your resume for this specific job type.
                    {followUpQuestions.length > 0 && showFollowUpQuestions && " Your answers to the follow-up questions above will be automatically included."}
                  </p>
                </div>
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex justify-between">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setCurrentStep(1)}
                >
                  <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                  Back
                </button>
                
                <div className="relative" style={{ minHeight: polishing ? '100px' : 'auto' }}>
                  <DisabledCreditButton
                    serviceType="resume_polish"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={handleAdditionalInfoSubmit}
                    disabled={polishing}
                  >
                    {polishing ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Polishing Resume...
                      </>
                    ) : (
                      <>
                        <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                        Polish My Resume ({polishCreditCost} credits)
                      </>
                    )}
                  </DisabledCreditButton>
                  {polishing && (
                    <ProgressBar progress={polishingProgress} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Step 4: Final Actions with Tabs */}
      {currentStep === 4 && polishResults && (
        <div className="card overflow-hidden mb-8">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h2 className="text-xl font-medium text-gray-900">
              Step 4: Final Actions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Your resume has been optimized for {selectedJobType.title} roles. A new version called "{polishResults.versionName}" has been created.
            </p>
          </div>
          
          {/* Success Message */}
          <div className="px-4 py-3 bg-green-50 border-b border-green-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiCheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Resume Polished Successfully!</h3>
              </div>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('compare')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'compare'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Compare Versions
              </button>
              <button
                onClick={() => setActiveTab('edit')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'edit'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Edit Resume
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'jobs'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Find Jobs
              </button>
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="px-4 py-5 sm:p-6">
            {activeTab === 'compare' && (
              <div className="space-y-6">
                <ResumeComparison 
                  originalResumeContent={originalResumeContent} 
                  polishedResumeContent={polishedResumeContent} 
                />
                
                <div className="flex justify-between">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={() => setCurrentStep(2)}
                  >
                    <FiArrowLeft className="-ml-1 mr-2 h-4 w-4" />
                    Back
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className="-ml-1 mr-2 h-4 w-4" />
                          Save
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={generateResumePDF}
                    >
                      <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                      Download as PDF
                    </button>
                  </div>
                </div>
              </div>
            )}
            
                         {activeTab === 'edit' && (
               <div className="space-y-6">
                 <div className="mb-4">
                   <h3 className="text-lg font-medium text-gray-900 mb-2">Edit Your Resume</h3>
                   <p className="text-gray-600">
                     Make further adjustments to your polished resume before applying to jobs.
                   </p>
                 </div>
                 
                 {/* Direct ResumeDocument Component */}
                 {polishResults && polishResults.resumeId && (
                   <div className="border border-gray-200 rounded-lg overflow-hidden">
                     <ResumeDocument 
                       resumeId={polishResults.resumeId}
                       versionId={polishResults.versionId}
                       isEmbedded={true}
                       onSaveSuccess={() => {
                         toast.success('Resume saved successfully!');
                       }}
                     />
                   </div>
                 )}
                 

               </div>
             )}
            
            {activeTab === 'jobs' && (
              <div className="space-y-6">
                <div className="text-center py-8">
                  <FiBriefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Find Jobs for {selectedJobType.title}</h3>
                  <p className="text-gray-600 mb-6">
                    Discover job opportunities that match your optimized resume and career goals.
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    onClick={handleFindJobs}
                  >
                    <FiBriefcase className="-ml-1 mr-2 h-4 w-4" />
                    Search for Jobs
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Job Search Modal */}
      {showJobSearchModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiCheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Resume Saved Successfully!</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Your resume has been saved. Would you like to find jobs that match your optimized profile for {selectedJobType.title} roles?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleFindJobs}
                >
                  <FiBriefcase className="-ml-1 mr-2 h-4 w-4" />
                  Find Jobs
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowJobSearchModal(false)}
                >
                  <FiX className="-ml-1 mr-2 h-4 w-4" />
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Resume Style Modal */}
      <ResumeStyleModal
        show={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        resumeId={polishResults?.resumeId}
        versionId={polishResults?.versionId}
      />
    </div>
  );
};

export default ResumeMassage;
